import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useSocialProfile } from "../scripts/SocialProfileContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastSuccess, toastError } from "@/lib/toast";

const HANDLE_RE = /^[a-zA-Z0-9_]{3,30}$/;

/**
 * Hard gate: handle + display name before any Social surface.
 */
const SocialSetupPage = () => {
  const { accessToken } = useAuth();
  const { profile, refresh, setComplete, setProfile } = useSocialProfile();
  const navigate = useNavigate();
  const [username, setUsername] = useState(profile?.username || "");
  const [displayName, setDisplayName] = useState(profile?.display_name || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const handleLocked = Boolean(profile?.username);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const handle = username.trim().toLowerCase();
    const name = displayName.trim();
    if (!handleLocked && !HANDLE_RE.test(handle)) {
      setError("Handle must be 3–30 characters: letters, numbers, underscore");
      return;
    }
    if (!name) {
      setError("Display name is required");
      return;
    }
    setBusy(true);
    try {
      const body = { display_name: name };
      if (!handleLocked) body.username = handle;
      const res = await axios.put(`${API_BASE_URL}/social/me/profile`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setComplete(Boolean(res.data?.complete));
      setProfile(res.data?.profile || null);
      await refresh();
      toastSuccess("Social profile ready");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not save social profile";
      setError(Array.isArray(msg) ? msg.join(", ") : msg);
      toastError(Array.isArray(msg) ? msg.join(", ") : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <h1 className="text-2xl font-semibold tracking-tight mb-2">
        Set up your Social profile
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Choose a handle and display name before using Social. Your workplace
        identity stays separate.
      </p>
      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="social-handle">Handle</Label>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-sm">@</span>
            <Input
              id="social-handle"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your_handle"
              disabled={busy || handleLocked}
              autoComplete="off"
            />
          </div>
          {handleLocked ? (
            <p className="text-xs text-muted-foreground">
              Handle cannot be changed once set.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              3–30 characters: a–z, 0–9, underscore. Stored lowercase. Reserved
              and disallowed words are blocked.
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="social-display-name">Display name</Label>
          <Input
            id="social-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How you appear on Social"
            disabled={busy}
            maxLength={80}
          />
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Saving…" : "Continue"}
        </Button>
      </form>
    </div>
  );
};

export default SocialSetupPage;
